import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = window.location.origin;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated === false || !data.user_id) {
          setUser(null);
        } else {
          setUser(data);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            if (data.requires_verification) {
              resolve(data); // Let the caller handle verification redirect
            } else {
              setUser(data);
              resolve(data);
            }
          } else {
            reject(new Error(data.detail || 'Login failed'));
          }
        } catch (e) {
          reject(new Error('Login failed - server error'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error during login'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  const register = async (email, password, name) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            if (data.requires_verification) {
              resolve(data); // Let the caller handle verification redirect
            } else {
              setUser(data);
              resolve(data);
            }
          } else {
            reject(new Error(data.detail || 'Registration failed'));
          }
        } catch (e) {
          reject(new Error('Registration failed - server error'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error during registration'));
      };
      
      xhr.send(JSON.stringify({ email, password, name }));
    });
  };

  const verifyEmail = async (email, code) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/verify-email`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;

      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            setUser(data);
            resolve(data);
          } else {
            reject(new Error(data.detail || 'Verification failed'));
          }
        } catch (e) {
          reject(new Error('Verification failed - server error'));
        }
      };

      xhr.onerror = function() {
        reject(new Error('Network error during verification'));
      };

      xhr.send(JSON.stringify({ email, code }));
    });
  };

  const resendCode = async (email) => {
    const response = await fetch(`${API_URL}/api/auth/resend-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to resend code');
    return data;
  };

  const exchangeSession = async (sessionId) => {
    const response = await fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      throw new Error('Session exchange failed');
    }

    const userData = await response.json();
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/browse';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      loginWithGoogle,
      exchangeSession,
      checkAuth,
      verifyEmail,
      resendCode,
      refreshUser: checkAuth, // Alias for refreshing user data after profile updates
      isAuthenticated: !!user,
      isAdmin: user?.is_admin || false,
      isOwner: user?.role === 'owner',
      isAdminRole: user?.role === 'admin',
      isOwnerOrAdmin: user?.role === 'owner' || user?.role === 'admin',
      isCompany: user?.role === 'company' || user?.is_company,
      isApprovedCreator: user?.is_approved_creator || false,
      companyStatus: user?.company_status || null,
      hasSubscription: user?.has_subscription || false,
      isPremium: user?.is_premium || false,
      isFreeTier: user?.is_free_tier !== false,
      showAds: !(user?.is_premium) && !(user?.role === 'owner') && !(user?.role === 'admin'),
      subscription: user?.subscription,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
